import React, { useMemo, useState } from "react"
import {
  Badge,
  Button,
  Col,
  Row,
  Card,
  CardBody,
  Modal,
  ModalBody,
  ModalHeader,
  Input,
  Label,
} from "reactstrap"

// Custom Components
import TableContainer from "../../../components/Common/TableContainer"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import { useContactFormMessages } from "hooks/useContactForm"
import { useListParams } from "hooks/useListParams"
import { formatReadableDate } from "utils/formatDate"
import TruncateText from "components/Common/TruncateText"
import GlobalInputSearch from "components/Common/GlobalInputSearch"

const detailRows = message => [
  ["ID", message?.id],
  ["Name", message?.name],
  ["Email", message?.email],
  ["Mobile", message?.mobile],
  ["Created At", message?.created_at ? formatReadableDate(message.created_at) : "-"],
  ["Updated At", message?.updated_at ? formatReadableDate(message.updated_at) : "-"],
]

const MessageList = () => {
  document.title = "Message List"
  const [selectedMessage, setSelectedMessage] = useState(null)

  // Custom hook for managing URL search parameters
  const {
    params,
    setPage,
    setSearch,
    updateParams,
  } = useListParams({
    page: 1,
    search: "",
    name: "",
  })

  // Memoized query parameters for the API call
  const queryParams = useMemo(
    () => ({
      page: params.page,
      per_page: 10,
      ...(params.search && { search: params.search }),
      ...(params.name && { name: params.name }),
    }),
    [params.page, params.search, params.name]
  )

  const handleNameFilter = value => {
    updateParams(prev => ({
      ...prev,
      name: value,
      page: 1,
    }))
  }

  const { data: messageResponse, isLoading } =
    useContactFormMessages(queryParams)

  const messageData = messageResponse?.data?.data || []
  const paginationInfo = messageResponse?.data

  // Table column definition
  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "id",
        cell: ({ row }) => (
          <span className="text-body fw-bold">{row.original.id}</span>
        ),
      },
      {
        header: "Name",
        accessorKey: "name",
      },
      {
        header: "Email",
        accessorKey: "email",
      },
      {
        header: "Mobile",
        accessorKey: "mobile",
      },
      {
        header: "Message",
        accessorKey: "message",
        meta: {
          style: { width: "320px", minWidth: "260px", maxWidth: "360px" },
        },
        cell: ({ row }) => (
          <div
            className="text-body"
            style={{
              whiteSpace: "normal",
              overflowWrap: "anywhere",
              wordBreak: "break-word",
              lineHeight: 1.4,
            }}
          >
            <TruncateText text={row.original.message} limit={30} />
          </div>
        ),
      },
      {
        header: "Date",
        accessorKey: "created_at",
        cell: ({ row }) =>
          row.original.created_at
            ? formatReadableDate(row.original.created_at)
            : "-",
      },
      {
        header: "Action",
        enableColumnFilter: false,
        cell: ({ row }) => (
          <Button
            color="primary"
            size="sm"
            outline
            onClick={() => setSelectedMessage(row.original)}
          >
            <i className="mdi mdi-eye-outline me-1" />
            View
          </Button>
        ),
      },
    ],
    []
  )

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Message" breadcrumbItem="Message Lists" />
          <Row>
            <Col lg="12">
              <Card>
                <CardBody className="border-bottom ">
                  <Row className="justify-content-between align-items-center">
                    <Col xs={12} sm={6}>
                      <h5 className="mb-0">
                        Message List{" "}
                        <span className="badge badge-soft-info">
                          {paginationInfo?.total ?? 0}
                        </span>
                      </h5>
                    </Col>
                  </Row>
                </CardBody>
                <CardBody>
                  <Row className="mb-3">
                    <Col sm={4}>
                      <Label className="mb-1 small fw-semibold">
                        Global Search
                      </Label>
                      <GlobalInputSearch
                        value={params.search}
                        onChange={setSearch}
                      />
                    </Col>
                    <Col sm={4}>
                      <Label className="mb-1 small fw-semibold">
                        Filter by Name
                      </Label>
                      <Input
                        value={params.name || ""}
                        placeholder="Type sender name..."
                        onChange={event => handleNameFilter(event.target.value)}
                      />
                    </Col>
                  </Row>
                  <TableContainer
                    columns={columns}
                    data={messageData}
                    isPagination={true}
                    paginationInfo={paginationInfo}
                    onPageChange={setPage}
                    loading={isLoading}
                    tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-4 border-top"
                    pagination="pagination"
                    paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Modal
            isOpen={!!selectedMessage}
            toggle={() => setSelectedMessage(null)}
            centered
            size="lg"
          >
            <ModalHeader toggle={() => setSelectedMessage(null)}>
              Message Details
            </ModalHeader>
            <ModalBody>
              {selectedMessage && (
                <>
                  <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-4">
                    <div>
                      <h5 className="mb-1">
                        {selectedMessage.name || "Unknown Sender"}
                      </h5>
                      <div className="text-muted small">
                        {selectedMessage.email || "No email"}{" "}
                        {selectedMessage.mobile ? `| ${selectedMessage.mobile}` : ""}
                      </div>
                    </div>
                    <Badge color="info" className="px-3 py-2">
                      #{selectedMessage.id}
                    </Badge>
                  </div>

                  <div className="border rounded p-3 mb-4 bg-light">
                    <div className="small text-muted mb-2">Message</div>
                    <div className="text-body" style={{ whiteSpace: "pre-wrap" }}>
                      {selectedMessage.message || "-"}
                    </div>
                  </div>

                  <Row className="g-3">
                    {detailRows(selectedMessage).map(([label, value]) => (
                      <Col md={6} key={label}>
                        <div className="border rounded p-3 h-100">
                          <div className="small text-muted mb-1">{label}</div>
                          <div className="fw-semibold text-break">
                            {value || "-"}
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </>
              )}
            </ModalBody>
          </Modal>
        </div>
      </div>
    </React.Fragment>
  )
}

export default MessageList
